'use client';

import Image, { type ImageProps } from 'next/image';
import { type ReactEventHandler, useEffect, useState } from 'react';

type OptimizedImageProps = ImageProps & {
   alt: string;
   placeholder?: 'blur' | 'empty';
   placeholderColor?: string;
   lazy?: boolean;
   priority?: boolean;
   onLoad?: ReactEventHandler<HTMLImageElement>;
   onError?: ReactEventHandler<HTMLImageElement>;
};

export const OptimizedImage = ({
   alt,
   quality = 80,
   placeholder = 'blur',
   placeholderColor = 'rgba(0,0,0,0.1)',
   lazy = true,
   priority = false,
   onLoad,
   onError,
   className,
   style,
   ...props
}: OptimizedImageProps) => {
   const [mounted, setMounted] = useState(false);
   const [hasError, setHasError] = useState(false);

   useEffect(() => {
      setMounted(true);
   }, []);

   const handleLoad: ReactEventHandler<HTMLImageElement> = (event) => {
      onLoad?.(event);
   };

   const handleError: ReactEventHandler<HTMLImageElement> = (event) => {
      setHasError(true);
      onError?.(event);
   };

   const generatePlaceholderDataUrl = (color: string) => {
      return `data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3Crect width='100%25' height='100%25' fill='${encodeURIComponent(color)}'/%3E%3C/svg%3E`;
   };

   const combinedClassName = [className, 'transition-opacity duration-300']
      .filter(Boolean)
      .join(' ');

   const combinedStyle = {
      ...style,
      ...(hasError && {
         backgroundColor: placeholderColor,
         display: 'flex',
         alignItems: 'center',
         justifyContent: 'center',
      }),
   };

   if (hasError) {
      return (
         <div
            className={combinedClassName}
            style={combinedStyle}
            aria-label='Image non disponible'
         >
            <svg
               width='24'
               height='24'
               viewBox='0 0 24 24'
               fill='none'
               stroke='currentColor'
               className='text-gray-400'
            >
               <path d='M14.828 14.828a4 4 0 0 1-5.656 0' />
               <path d='M9 9a4 4 0 0 1 5.656 0' />
               <path d='M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z' />
            </svg>
         </div>
      );
   }

   if (!mounted) {
      return (
         <div
            className={combinedClassName}
            style={{
               ...style,
               backgroundColor: placeholderColor,
               width: props.width,
               height: props.height,
            }}
         />
      );
   }

   return (
      <Image
         {...props}
         alt={alt}
         quality={quality}
         placeholder={placeholder === 'blur' ? 'blur' : 'empty'}
         blurDataURL={
            placeholder === 'blur'
               ? generatePlaceholderDataUrl(placeholderColor)
               : undefined
         }
         loading={priority ? 'eager' : lazy ? 'lazy' : 'eager'}
         priority={priority}
         className={combinedClassName}
         style={combinedStyle}
         onLoad={handleLoad}
         onError={handleError}
         role='img'
      />
   );
};
